import React from 'react';
import { useAccountStore } from '@components/Stores/accountStore';
import { useMaterialsStore } from '@components/Stores/MaterialsStore';
import { getMaterialsCardData } from '../Dashboard/dashboardUtils';
import { MaterialTrendButton } from '../Dashboard/MaterialTrendButton';
import { getStartDate, sliceFromDate } from '../Policy/MaterialDetails/components/MaterialDetailUtils';
import { ChartViewToolbar } from '../Policy/ProductOverview/ProductOverviewChart/ChartViewToolbar';
import { HistoricalChart } from './components/HistoricalChart';
import {
  IndexRoutes,
  KeyPoints,
  HistoricalViewOptions,
  PricePoint,
  priceObjectToFutures,
  getCurveForIndex,
} from './components/HistoricalUtils';
//import { default as rebar } from './data/rebar-opportunity.json';
import { useExposureStore, GENERAL_ID } from '../../components/Stores/exposureDataStore';
import { useNavigate, useParams } from 'react-router-dom';
import { getHistoryPrices } from '../../sp-firebase/historyPricesFB';
import { LoadingSpinnerOverlay } from '../../components/UI-Elements/LoadingSpinnerOverlay';
import { getCurrencySymbol } from '../../hooks/useCurrencyRates';
import { useCurrencyRates } from '@hooks/useCurrencyRates';
import { Button } from '@mui/material';
import { TodayPricePopup } from './components/TodayPricePopup';

export const HistoricalPrices: React.FC = () => {
  const navigate = useNavigate();
  const { materialId } = useParams();
  const { account, materialColorDictionary: colorsMap } = useAccountStore();
  const { materialsMap } = useMaterialsStore();
  const {
    netExposure,
    currentSelectedMaterialId: selMaterialId,
    setCurrentSelectedMaterialId: setSelMaterialId,
  } = useExposureStore();
  const { convertionMap, pricesMap } = useCurrencyRates();

  React.useEffect(() => {
    const fetchHistoricalPrices = async (matId: string) => {
      setIsLoading(true);
      setGridX(-100);

      const prices = await getHistoryPrices(matId.replace('c1', '').replace('C1', ''));
      setPricesData(prices);

      if (prices && prices.length > 0) {
        const lastPrices = prices[prices.length - 1];

        const currFutures = priceObjectToFutures(lastPrices) || [];
        setFutures(currFutures);
        setCurrentPrice(lastPrices.price);
      }
      const monthsCount = Object.keys(prices?.[0] || 'holders').length - 3;
      setMaxFutures(monthsCount);

      if (selectedFuture >= monthsCount) {
        setSelectedFuture(2);
      }
      setCurrencySymbol(getCurrencySymbol(materialsMap[matId].currency));

      setIsLoading(false);
    };

    if ((!materialId || materialId === '') && account.materialsToAccount?.length > 0) {
      const firstMaterial = account.materialsToAccount.filter((a) => a.materialId !== GENERAL_ID)?.[0].materialId;
      setMaterial(firstMaterial);

      return;
    }
    setSelMaterialId(materialId || GENERAL_ID);
    if (materialId && materialsMap && materialsMap[materialId]) {
      fetchHistoricalPrices(materialId);
    }
  }, [materialId, materialsMap, account.materialsToAccount]);

  const [isLoading, setIsLoading] = React.useState<boolean>(false);

  const [monthsToShow, setMonthsToShow] = React.useState<number>(12);
  const [selectedFuture, setSelectedFuture] = React.useState<number>(2);
  const [maxFutures, setMaxFutures] = React.useState<number>(5);

  const [pricesData, setPricesData] = React.useState<any[]>();
  const [futures, setFutures] = React.useState<any[]>([]);
  const [currentPrice, setCurrentPrice] = React.useState<number>(pricesMap[materialId || ''] || 0);
  const [pricePoints, setPricePoints] = React.useState<PricePoint[]>([]);
  const [gridX, setGridX] = React.useState<number>(0);
  const [currencySymbol, setCurrencySymbol] = React.useState<string>('$');
  const [showTodayPopup, setShowTodayPopup] = React.useState(false);

  React.useEffect(() => {
    if ((!selMaterialId || selMaterialId === GENERAL_ID) && account?.materialsToAccount?.length > 1) {
      setSelMaterialId(account.materialsToAccount[1].materialId);
    }
  }, [account.materialsToAccount, selMaterialId]);

  const getFuture = (pData: any, futureCompare: number): number => {
    const months = Array.from({ length: 12 }, (_, i) => pData['month' + String(i + 1).padStart(2, '0')]).filter(
      Boolean,
    );

    return months[futureCompare];
  };

  React.useEffect(() => {
    if (pricesData && pricesData.length > 0) {
      const lastPrices = pricesData[pricesData.length - 1];

      onDataClick(
        { date: '', future: getFuture(lastPrices, selectedFuture), price: lastPrices.price.toString() },
        -100,
      );
    }
  }, [selectedFuture, monthsToShow, pricesData]);

  const getVisibleData = (
    pricesDat: any[],
    showCount: number,
    futureCompare: number,
    futuresPr: any[],
  ): IndexRoutes[] => [
    ...sliceFromDate(pricesDat, getStartDate(showCount)).map((s, i) => ({
      price: s.price,
      future: getFuture(s, futureCompare),
      date: s.date,
      index: i,
    })),
    ...futuresPr,
  ];
  const onDataClick = ({ price, future, isFuture, ...data }: IndexRoutes, xCoord: number) => {
    const currPrice = pricesData?.[pricesData.length - 1];

    const nPrice = +(price || 0);
    const date = isFuture ? new Date(currPrice.date) : data.date ? new Date(data.date) : new Date();
    const updatedPoints = [
      {
        name: 'Index price ' + (data.date ? `(${data.date})` : ''),
        price: +(price || currentPrice) || undefined,
        color: colorsMap[selMaterialId],
        id: 'index',
        visible: true,
      },
      {
        name: `Future (${selectedFuture + 1}) price`,
        price: future,
        color: '#19ceb8',
        id: 'future1',
        visible: true,
      },
      ...(future
        ? [
            {
              name: `${future - nPrice < 0 ? 'Backwardation' : 'Contago'} (${selectedFuture + 1})`,
              price: future - nPrice,
              percent: ((future - nPrice) / nPrice || 1) * 100,
              color: '#19ceb8',
              id: 'compare',
              visible: true,
            },
          ]
        : []),
      ...getCurveForIndex(
        data.index || (pricesData?.length || 1) - 1,
        date,
        sliceFromDate(pricesData || [], getStartDate(monthsToShow)),
      ),
    ];
    setPricePoints(updatedPoints);
    setGridX(xCoord);
  };

  const getChartViewOpts = (count: number, points: PricePoint[]): { text: string; value: any; desc?: string }[] =>
    Array.from({ length: count }).map((_, i) => ({
      text: (i + 1).toString(),
      value: i,
      desc: points.filter((p) => p.id === `index ${i}` && p.key.startsWith('month'))?.[0]?.name,
    }));

  const setMaterial = (materialId: string) => {
    setSelMaterialId(materialId);

    navigate('/historical-prices/' + (materialId && materialId !== GENERAL_ID ? materialId : ''));
  };

  return (
    <div className="flex flex-col flex-1 h-full gap-3">
      <MaterialTrendButton
        buttons={getMaterialsCardData(
          materialsMap,
          colorsMap,
          convertionMap,
          account.materialsToAccount.filter((a) => a.materialId !== GENERAL_ID),
          netExposure,
          account.addMaterials,
        )}
        currentMaterial={selMaterialId}
        setCurrentMaterial={setMaterial}
      />
      {selMaterialId ? (
        <div className="flex flex-col bg-white shadow-custom py-[0.5rem] gap-[1rem] px-[1.5rem]">
          <div className="flex flex-1 flex-col">
            <h4 className="">Historical prices</h4>
            {pricesData ? (
              <div className="flex flex-row flex-1 gap-10 overflow-hidden">
                <div className="flex flex-col flex-1">
                  <div className="flex justify-between ">
                    <div className="flex gap-1">
                      Futures:
                      <ChartViewToolbar
                        viewOptions={getChartViewOpts(maxFutures, pricePoints)}
                        selectedValue={selectedFuture}
                        onSelectionChange={setSelectedFuture}
                      />
                    </div>
                    <div>
                      <Button variant="outlined" onClick={() => setShowTodayPopup(true)}>
                        Today
                      </Button>
                      {showTodayPopup && (
                        <TodayPricePopup onClose={() => setShowTodayPopup(false)} materialId={selMaterialId} />
                      )}
                    </div>
                    <div className="flex gap-1">
                      Show:
                      <ChartViewToolbar
                        viewOptions={HistoricalViewOptions}
                        selectedValue={monthsToShow}
                        onSelectionChange={setMonthsToShow}
                      />
                    </div>
                  </div>
                  <div className="flex-1">
                    <HistoricalChart
                      indexRoutesData={getVisibleData(pricesData || [], monthsToShow, selectedFuture, futures)}
                      materialColor={colorsMap[selMaterialId]}
                      onDataClick={onDataClick}
                      currency={materialsMap[selMaterialId]?.currency || 'USD'}
                      lineXcoord={gridX}
                      isDrawPrice
                    />
                  </div>
                  <div className="pt-4 flex-1 *min-h-[250px] ">
                    <HistoricalChart
                      indexRoutesData={getVisibleData(pricesData || [], monthsToShow, selectedFuture, futures)}
                      materialColor={colorsMap[selMaterialId]}
                      onDataClick={onDataClick}
                      currency={materialsMap[selMaterialId]?.currency || 'USD'}
                      lineXcoord={gridX}
                    />
                  </div>
                </div>
                <div className="w-[11.5rem]">
                  <KeyPoints
                    routePrices={pricePoints}
                    selectedRoute={`Future ${selectedFuture}`}
                    currency={currencySymbol}
                  />
                </div>
              </div>
            ) : (
              <div className="flex h-[30rem] justify-center items-center">
                {!isLoading && (
                  <div className="text-center">
                    No futures for {materialsMap[materialId || '']?.name} ({selMaterialId})
                  </div>
                )}
              </div>
            )}
            {isLoading && <LoadingSpinnerOverlay size={64} />}
          </div>
        </div>
      ) : (
        <HistPricesSkeleton />
      )}
    </div>
  );
};

const HistPricesSkeleton: React.FC = () => <div>loading...</div>;
